"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { Link } from "@/i18n/navigation";
import type { Card } from "@/lib/api/types";
import { getCardDisplayModel } from "@/lib/card-display";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import RichDescription from "./RichDescription";
import BetaBadge from "./BetaBadge";
import { useT } from "@/lib/i18n";
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
import { imageUrl } from "@/lib/image-url";
const colorMap: Record<string, string> = {
  ironclad: "border-[var(--color-ironclad)]/60 hover:border-[var(--color-ironclad)]",
  silent: "border-[var(--color-silent)]/60 hover:border-[var(--color-silent)]",
  defect: "border-[var(--color-defect)]/60 hover:border-[var(--color-defect)]",
  necrobinder: "border-[var(--color-necrobinder)]/60 hover:border-[var(--color-necrobinder)]",
  regent: "border-[var(--color-regent)]/60 hover:border-[var(--color-regent)]",
  colorless: "border-[var(--color-colorless)]/60 hover:border-[var(--color-colorless)]",
  curse: "border-[var(--color-curse)]/60 hover:border-[var(--color-curse)]",
  status: "border-line-strong/60 hover:border-line-strong"
};
const rarityColors: Record<string, string> = {
  Basic: "text-fg-muted",
  Common: "text-fg-secondary",
  Uncommon: "text-info",
  Rare: "text-[var(--accent-gold)]",
  Ancient: "text-special",
  Curse: "text-danger",
  Status: "text-fg-muted",
  Event: "text-success",
  Token: "text-fg-muted",
  Quest: "text-warning"
};
const energyIconMap: Record<string, string> = {
  ironclad: "ironclad",
  silent: "silent",
  defect: "defect",
  necrobinder: "necrobinder",
  regent: "regent",
  colorless: "colorless"
};

// Card grid tiles route through the same `RichDescription` tokenizer the
// detail pages use so every BBCode tag the site supports (every color,
// [b]/[i], [sine]/[jitter], [energy:N], [star:N]) renders consistently.
// The previous inline regex only handled [gold] / [green] / energy / star,
// so tags like [purple] (Blade of Ink) leaked through as literal text.
function renderDescription(card: Card, text: string): React.ReactNode {
  const normalizedText = text.replace(/\n/g, " ");
  const energyIcon = energyIconMap[card.color] || "colorless";
  return <RichDescription text={normalizedText} energyIcon={energyIcon} />;
}
function CardItem(t0) {
  const $ = _c(63);
  const {
    card
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  const [upgraded, setUpgraded] = useState(false);
  const [betaArt, setBetaArt] = useState(false);
  let hasBetaArt;
  let hasUpgrade;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== betaArt || $[1] !== bp || $[2] !== card || $[3] !== t || $[4] !== upgraded) {
    const display = getCardDisplayModel(card, upgraded);
    const isUpgraded = display.isUpgraded;
    hasBetaArt = !!card.beta_image_url;
    hasUpgrade = !!card.upgrade;
    t4 = `group relative flex flex-col bg-[var(--bg-card)] rounded-lg border-2 ${isUpgraded ? "border-success/60 hover:border-success" : colorMap[card.color] || "border-[var(--border-subtle)] hover:border-[var(--border-accent)]"} p-4 transition-all hover:bg-[var(--bg-card-hover)] hover:shadow-lg hover:shadow-scrim/20`;
    let t9;
    if ($[15] !== bp || $[16] !== card.beta || $[17] !== card.id) {
      t9 = card.beta ? `${bp}/beta/cards/${card.id.toLowerCase()}` : `${bp}/cards/${card.id.toLowerCase()}`;
      $[15] = bp;
      $[16] = card.beta;
      $[17] = card.id;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    if ($[19] !== t9) {
      t5 = <Link prefetch={false} href={t9} className="absolute inset-0 z-10" />;
      $[19] = t9;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    const imgUrl = betaArt && card.beta_image_url ? card.beta_image_url : card.image_url || card.beta_image_url;
    let t10;
    if ($[21] !== card.name || $[22] !== imgUrl || $[23] !== t) {
      t10 = imgUrl ? <div className="mb-3 -mx-4 -mt-4"><img src={imageUrl(imgUrl)} alt={t("{name} - Slay the Spire 2 Card", {
          name: card.name
        })} className="w-full h-32 object-cover rounded-t-lg" loading="lazy" crossOrigin="anonymous" /></div> : null;
      $[21] = card.name;
      $[22] = imgUrl;
      $[23] = t;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    t6 = t10;
    let t11;
    if ($[25] !== card.beta) {
      t11 = card.beta && <BetaBadge />;
      $[25] = card.beta;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== card.is_x_star_cost || $[28] !== card.star_cost || $[29] !== t) {
      t12 = (card.star_cost != null || card.is_x_star_cost) && <span className="inline-flex items-center gap-0.5 h-7 px-2 rounded-full bg-[var(--bg-primary)] border border-warning/40 text-sm font-bold text-warning">{card.is_x_star_cost ? "X" : card.star_cost}<img src={imageUrl("/static/images/icons/star_icon.webp")} alt={t("star")} className="w-3.5 h-3.5" crossOrigin="anonymous" /></span>;
      $[27] = card.is_x_star_cost;
      $[28] = card.star_cost;
      $[29] = t;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    t7 = <div className="flex items-start justify-between mb-2"><h3 className="font-semibold text-[var(--text-primary)] leading-tight flex items-center gap-1.5">{card.name}{isUpgraded && <span className="text-success">+</span>}{t11}</h3><div className="ml-2 flex-shrink-0 flex items-center gap-1"><span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--bg-primary)] border text-sm font-bold ${isUpgraded && display.upgrade?.cost != null ? "border-success/50 text-success" : "border-[var(--border-subtle)] text-[var(--accent-gold)]"}`}>{card.is_x_cost ? "X" : display.cost != null && display.cost < 0 ? "U" : display.cost}</span>{t12}</div></div>;
    let t13;
    if ($[31] !== card.type) {
      t13 = <span className="text-[var(--text-secondary)]">{card.type}</span>;
      $[31] = card.type;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="text-[var(--text-muted)]">·</span>;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    const t15 = rarityColors[card.rarity] || "text-fg-muted";
    let t16;
    if ($[34] !== card.rarity || $[35] !== t15) {
      t16 = <span className={t15}>{card.rarity}</span>;
      $[34] = card.rarity;
      $[35] = t15;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    let t17;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="text-[var(--text-muted)]">·</span>;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] !== card.color) {
      t18 = <span className="text-[var(--text-muted)] capitalize">{card.color}</span>;
      $[38] = card.color;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    if ($[40] !== t13 || $[41] !== t16 || $[42] !== t18) {
      t8 = <div className="flex items-center gap-2 mb-3 text-xs">{t13}{t14}{t16}{t17}{t18}</div>;
      $[40] = t13;
      $[41] = t16;
      $[42] = t18;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    t1 = "space-y-1.5 text-sm text-[var(--text-secondary)] leading-relaxed";
    t2 = <p>{renderDescription(card, display.descriptionText)}</p>;
    t3 = display.keywordText && <p>{renderDescription(card, display.keywordText)}</p>;
    $[0] = betaArt;
    $[1] = bp;
    $[2] = card;
    $[3] = t;
    $[4] = upgraded;
    $[5] = hasBetaArt;
    $[6] = hasUpgrade;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    hasBetaArt = $[5];
    hasUpgrade = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[44] !== t1 || $[45] !== t2 || $[46] !== t3) {
    t9 = <div className={t1}>{t2}{t3}</div>;
    $[44] = t1;
    $[45] = t2;
    $[46] = t3;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex-grow" />;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== betaArt || $[50] !== hasBetaArt || $[51] !== hasUpgrade || $[52] !== t || $[53] !== upgraded) {
    t11 = (hasBetaArt || hasUpgrade) && <div className="flex justify-end gap-1.5 mt-3 relative z-20">{hasBetaArt && <button onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setBetaArt(!betaArt);
      }} className={`text-base w-7 h-7 flex items-center justify-center rounded transition-colors ${betaArt ? "bg-warning/10 border border-warning/50" : "bg-[var(--bg-primary)] border border-[var(--border-subtle)] opacity-50 hover:opacity-100"}`} title={betaArt ? t("Show normal art") : t("Show beta art")}>✏️</button>}{hasUpgrade && <button onClick={e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        setUpgraded(!upgraded);
      }} className={`text-base w-7 h-7 flex items-center justify-center rounded transition-colors ${upgraded ? "bg-success/10 border border-success/50" : "bg-[var(--bg-primary)] border border-[var(--border-subtle)] opacity-50 hover:opacity-100"}`} title={upgraded ? t("Show base card") : t("Show upgraded")}>🔨</button>}</div>;
    $[49] = betaArt;
    $[50] = hasBetaArt;
    $[51] = hasUpgrade;
    $[52] = t;
    $[53] = upgraded;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] !== t11 || $[56] !== t4 || $[57] !== t5 || $[58] !== t6 || $[59] !== t7 || $[60] !== t8 || $[61] !== t9) {
    t12 = <div className={t4}>{t5}{t6}{t7}{t8}{t9}{t10}{t11}</div>;
    $[55] = t11;
    $[56] = t4;
    $[57] = t5;
    $[58] = t6;
    $[59] = t7;
    $[60] = t8;
    $[61] = t9;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  return t12;
}
export default function CardGrid(t0) {
  const $ = _c(4);
  const {
    cards
  } = t0;
  let t1;
  if ($[0] !== cards) {
    t1 = cards.map(_temp);
    $[0] = cards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(card) {
  return <CardItem key={card.id} card={card} />;
}